import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { MenuRoute } from '@/api/system';

type PageModule = { default: ComponentType };
type PageLoader = () => Promise<PageModule>;
const pageModules = import.meta.glob<PageModule>('../pages/**/*.{tsx,jsx}') as Record<string, PageLoader>;

// 兼容旧后台组件名；新页面使用“模块/页面/index”并放在 pages/模块/页面/page.tsx 即可自动加载。
const legacyModules: Record<string, string> = {
  'member/applications/index': '../pages/member/review/page.tsx',
  'member/list/index': '../pages/member/list/page.tsx',
  'member/levels/index': '../pages/member/level/page.tsx',
  'member/bills/index': '../pages/finance/fees/page.tsx',
  'business/opportunities/index': '../pages/business/list/page.tsx',
  'business/contact-applications/index': '../pages/business/contact/page.tsx',
  'business/top-orders/index': '../pages/business/top-orders/page.tsx',
  'activity/list/index': '../pages/activity/list/page.tsx',
  'activity/signups/index': '../pages/activity/signin/page.tsx',
  'merchant/list/index': '../pages/merchant/list/page.tsx',
  'merchant/writeoff-records/index': '../pages/merchant/writeoff/page.tsx',
  'merchant/reconciliation/index': '../pages/merchant/stats/page.tsx',
  'member/directory/index': '../pages/enterprise/list/page.tsx',
  'member/donations/index': '../pages/donation/overview/page.tsx',
  'community/index': '../pages/community/review/page.tsx',
  'party/index': '../pages/party/list/page.tsx',
  'commerce-news/index': '../pages/commerce-news/list/page.tsx',
  'dashboard/operation/index': '../pages/dashboard/operation/page.tsx',
  'system/user/index': '../pages/settings/accounts/page.tsx',
  'system/role/index': '../pages/settings/roles/page.tsx',
  'system/menu/index': '../pages/settings/menus/page.tsx',
  'system/dict/index': '../pages/settings/dicts/page.tsx',
  'monitor/operlog/index': '../pages/settings/logs/page.tsx',
  'other-services/profile/index': '../pages/other-services/page.tsx',
  'other-services/banners/index': '../pages/other-services/banners.tsx',
};

const legacyRoutes: Record<string, string> = {
  'member/applications/index': '/member/review', 'member/list/index': '/member/list',
  'member/levels/index': '/member/level', 'member/bills/index': '/member/fees',
  'business/opportunities/index': '/business/list', 'business/contact-applications/index': '/business/contact',
  'business/top-orders/index': '/business/top-orders', 'activity/list/index': '/activity/list',
  'activity/signups/index': '/activity/signin', 'merchant/list/index': '/merchant/list',
  'merchant/writeoff-records/index': '/merchant/writeoff', 'merchant/reconciliation/index': '/merchant/stats',
  'member/directory/index': '/enterprise/list', 'member/donations/index': '/donation/overview',
  'community/index': '/community/review', 'party/index': '/party/list',
  'commerce-news/index': '/commerce-news/list', 'dashboard/operation/index': '/dashboard/operation',
  'system/user/index': '/settings/accounts', 'system/role/index': '/settings/roles',
  'system/menu/index': '/settings/menus', 'system/dict/index': '/settings/dicts',
  'monitor/operlog/index': '/settings/logs',
};

const lazyPages = new Map<string, LazyExoticComponent<ComponentType>>();

export function joinMenuPath(parentPath: string, path?: string) {
  if (!path) return parentPath || '/';
  if (path.startsWith('/')) return path;
  return `${parentPath}/${path}`.replace(/\/+/g, '/');
}

export function resolveMenuPath(route: MenuRoute, parentPath = '') {
  const path = joinMenuPath(parentPath, route.path);
  return (route.component && legacyRoutes[route.component]) || path;
}

export function resolveMenuComponent(component?: string) {
  if (!component || component === 'Layout') return undefined;
  const normalized = component.replace(/^\/+/, '').replace(/\.(tsx|jsx)$/, '');
  const withoutIndex = normalized.replace(/\/index$/, '');
  const candidates = [legacyModules[normalized], `../pages/${withoutIndex}/page.tsx`, `../pages/${withoutIndex}/index.tsx`, `../pages/${normalized}.tsx`];
  const modulePath = candidates.find((candidate) => candidate && pageModules[candidate]);
  if (!modulePath) return undefined;
  if (!lazyPages.has(modulePath)) lazyPages.set(modulePath, lazy(pageModules[modulePath]));
  return lazyPages.get(modulePath);
}
